import { useCallback, useEffect, useMemo, useState } from 'react';
import { CHECKLIST_PHASES } from '../data/weddingChecklist';

export const CHECKLIST_KEY = 'ido_wedding_checklist';
const EMPTY = { done: {}, custom: {} };

const load = () => {
  try {
    const raw = window.localStorage.getItem(CHECKLIST_KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    return EMPTY;
  }
};

export const useChecklist = () => {
  const [state, setState] = useState(load);

  useEffect(() => {
    window.localStorage.setItem(
      CHECKLIST_KEY,
      JSON.stringify({ ...state, updatedAt: new Date().toISOString() })
    );
  }, [state]);

  const toggle = useCallback((id) => {
    setState((s) => {
      const done = { ...s.done };
      if (done[id]) delete done[id];
      else done[id] = true;
      return { ...s, done };
    });
  }, []);

  const addCustom = useCallback((phaseId, label) => {
    const text = label.trim();
    if (!text) return;
    setState((s) => ({
      ...s,
      custom: {
        ...s.custom,
        [phaseId]: [...(s.custom[phaseId] || []), { id: `custom-${Date.now()}`, label: text }],
      },
    }));
  }, []);

  const removeCustom = useCallback((phaseId, id) => {
    setState((s) => {
      const done = { ...s.done };
      delete done[id];
      return {
        ...s,
        done,
        custom: { ...s.custom, [phaseId]: (s.custom[phaseId] || []).filter((t) => t.id !== id) },
      };
    });
  }, []);

  const reset = useCallback(() => setState(EMPTY), []);

  const phases = useMemo(
    () =>
      CHECKLIST_PHASES.map((phase) => {
        const tasks = [
          ...phase.tasks,
          ...(state.custom[phase.id] || []).map((t) => ({ ...t, custom: true })),
        ];
        const doneCount = tasks.filter((t) => state.done[t.id]).length;
        return { ...phase, tasks, doneCount, total: tasks.length };
      }),
    [state]
  );

  const total = phases.reduce((n, p) => n + p.total, 0);
  const doneCount = phases.reduce((n, p) => n + p.doneCount, 0);
  const percent = total ? Math.round((doneCount / total) * 100) : 0;

  return { phases, done: state.done, total, doneCount, percent, toggle, addCustom, removeCustom, reset };
};
